<template>
	<div class="account">
		<Profile
			v-if="loggedIn"
			:user="user"
			:img-profile="imgProfile"
			@logout="logout"
		/>
		<button
			v-else
			:title="$t('LoginButtonTitleAttributeHeaderSignIn')"
			:aria-label="$t('LoginButtonAriaLabelHeaderSignIn')"
			class="btn"
			@click="loginClick"
		>
			<img
				src="@/assets/images/account_circle.svg"
				width="34"
				height="34"
				alt="Account"
			/>
		</button>
		<SweetModal
			ref="modal"
			class="login-dialog"
			@close="setDialogState(false)"
		>
			<p class="dialog-caption" v-html="$t('LoginDialogTitleSignIn')" />
			<p v-if="hasLoginError" class="dialog-text dialog-error">
				{{ logInError }}
			</p>
			<LoadingSpinner v-if="isLoading || isLoggingIn" />
			<template v-else>
				<button
					:aria-label="$t('LoginButtonAriaLabelSignIn')"
					:title="$t('LoginButtonTitleAttributeSignIn')"
					class="google-btn"
					@click="onLogin"
				>
					<span class="inline-text google-logo-wrapper">
						<img
							:src="googleLogo"
							alt="google-logo"
							class="google-logo"
							width="16"
							height="16"
						/>
					</span>
					{{ $t("LoginButtonTextSignIn") }}
				</button>
				<p class="dialog-text">
					No account?
					<a href="#" @click.prevent="onLogin">Create one</a>
				</p>
			</template>
			<span
				class="inline-text"
				v-html="$t('LoginDialogLegalDisclaimerSignIn')"
			/>
			<a href="#" class="link" @click.prevent="onRemindButtonClick">
				{{ $t("LoginDialogText3SignUp") }}
			</a>
		</SweetModal>
		<SweetModal
			ref="subscriptionModal"
			class="subscription-dialog"
			@close="closeSubscriptionDialog"
		>
			<p class="dialog-caption">KEEP ME IN THE LOOP!</p>
			<p class="dialog-text">
				I’d like to receive exclusive email updates from Wordfinder™
			</p>
			<button
				class="button button--subscription"
				@click="onSubscriptionAction(NEWSLETTERS.ON)"
			>
				Subscribe
			</button>
			<a
				href="#"
				class="link"
				@click.prevent="onSubscriptionAction(NEWSLETTERS.OFF)"
			>
				No, thanks
			</a>
		</SweetModal>
	</div>
</template>

<script>
import Profile from "./profile";
import LoadingSpinner from "@/components/loading-spinner";
import { SweetModal } from "sweet-modal-vue";
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import { isClientSide, readIndexedDb, isEmpty } from "@/common/functions";
import { NEWSLETTERS } from "@/common/consts";

export default {
	name: "Account",
	components: {
		SweetModal,
		Profile,
		LoadingSpinner,
	},
	data() {
		return {
			isLoading: false,
			NEWSLETTERS,
		};
	},
	computed: {
		...mapState({
			isLoggingIn: (state) => state.login.isLoggingIn,
			logInError: (state) => state.login.logInError,
			isSubscriptionDialogOpen: (state) =>
				state.login.isSubscriptionDialogOpen,
			newslettersSubscription: (state) =>
				state.login.user.newslettersSubscription,
		}),
		...mapGetters([
			"isDialogOpen",
			"user",
			"loggedIn",
			"imgProfile",
			"previouslyLoggedIn",
		]),
		googleLogo() {
			return isClientSide() ? "/static/images/google-logo.svg" : "";
		},
		hasLoginError() {
			return !isEmpty(this.logInError);
		},
	},
	watch: {
		isDialogOpen: "toggleDialog",
		previouslyLoggedIn: "autoLoadLibrary",
		isSubscriptionDialogOpen: "openSubscriptionDialog",
	},
	created() {
		readIndexedDb(
			"firebaseLocalStorageDb",
			"firebaseLocalStorage",
			this.$store
		);
	},
	methods: {
		...mapActions(["login", "logout", "toggleNewslettersSubscription"]),
		...mapMutations({
			setDialogState: "SET_DIALOG_STATE",
		}),
		onOpenDialog() {
			this.$store.dispatch("trackEvent", {
				eventCategory: "Login",
				eventAction: "Click on header sign up button",
				eventLabel: "LoginButtonTextHeaderSignUp",
			});
		},
		onLogin() {
			this.login();

			this.$store.dispatch("trackEvent", {
				eventCategory: "Login",
				eventAction: "Click on Google sign up button",
				eventLabel: "LoginButtonTextSignIn",
			});
		},
		onRemindButtonClick() {
			this.remindButtonClick = true;
			this.$refs.modal.close();
			this.$store.dispatch("trackEvent", {
				eventCategory: "Login",
				eventAction: "Click on Remind me later button",
				eventLabel: "LoginDialogText3SignUp",
				eventID: 41,
			});
		},
		onCloseDialog() {
			if (!this.remindButtonClick) {
				this.$store.dispatch("trackEvent", {
					eventCategory: "Login",
					eventAction: "Click on sign in cross icon",
				});
			}
			this.remindButtonClick = false;
		},
		toggleDialog(val) {
			if (val) {
				this.$refs.modal.open();
				this.onOpenDialog();
			} else {
				this.$refs.modal.close();
				this.onCloseDialog();
			}
		},
		async getFirebaseClient() {
			const firebase = await import("firebase/app");
			await import("firebase/auth");
			let firebaseConfig = await import("@/firebaseConfig");

			if (!firebase.apps.length) {
				firebase.initializeApp(firebaseConfig.default);
			}

			return firebase;
		},
		autoLoadLibrary(isPreviouslyLoggedIn) {
			if (
				typeof window.__PRERENDER_INJECTED === "undefined" &&
				isPreviouslyLoggedIn
			) {
				this.getFirebaseClient()
					.then((firebase) => {
						firebase.auth().onAuthStateChanged((user) => {
							this.$store.dispatch("fetchUser", user);
						});
					})
					.catch(console.log);
			}
		},
		loginClick() {
			this.isLoading = true;
			this.$store.commit("SET_LOG_IN_ERROR", null);
			if (typeof window.__PRERENDER_INJECTED === "undefined") {
				this.getFirebaseClient()
					.then((firebase) => {
						firebase.auth().onAuthStateChanged((user) => {
							this.$store.dispatch("fetchUser", user);
						});
						this.isLoading = false;
					})
					.catch(console.log);
				this.setDialogState(true);
			}
		},
		onSubscriptionAction(value) {
			this.toggleNewslettersSubscription(value);
			const eventValues = {
				[NEWSLETTERS.ON]: {
					action: "Click on Newsletter Subscribe Button",
					label: "NewsletterSubscribeButtonClicked",
				},
				[NEWSLETTERS.OFF]: {
					action: "Click on Newsletter No Thanks Button",
					label: "NewsletterNoThanksButtonClicked",
				},
			};
			this.$store.dispatch("trackEvent", {
				eventCategory: "Newsletters",
				eventAction: `Click on Newsletter ${eventValues[value].action} Button`,
				eventLabel: eventValues[value].label,
			});

			this.$refs.subscriptionModal.close();
		},
		openSubscriptionDialog(val) {
			if (val) {
				this.$refs.subscriptionModal.open();
				this.$store.dispatch("trackEvent", {
					eventCategory: "Newsletters",
					eventAction: "Newsletter subscription popup is shown",
					eventLabel: "NewsletterPopUpShown",
				});
			}
		},
		closeSubscriptionDialog() {
			if (this.newslettersSubscription !== null) return;
			this.$store.dispatch("trackEvent", {
				eventCategory: "Newsletters",
				eventAction: "Newsletter subscription popup dismissed",
				eventLabel: "NewsletterPopUpDismissed",
			});
		},
	},
};
</script>

<style lang="scss" scoped src="./styles.scss"></style>
